import { Avatar, Box, Button, Paper, Typography } from '@mui/material'
import CustomBackdrop from 'common/components/CustomBackdrop'
import CustomClock from 'common/components/CustomClock'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import useCurrentMenu from 'common/hooks/useCurrentMenu'
import { ModalTypes } from 'common/hooks/useCustomModal'
import DefaultLoginBackGround from 'features/auth/components/LoginBackground'
import i18n from 'lang/idiomas'
import { enqueueSnackbar } from 'notistack'
import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ThemeController from 'theming/ThemeManager'
import PanelInput from '../components/PanelInput'
import PanelNumerico from '../components/PanelNumerico'
import PresenceActions from '../components/PresenceActions'

const PanelFichajes = () => {
  const themeController = new ThemeController()
  const timerRef = useRef(null)
  const menu = useCurrentMenu()
  const navigate = useNavigate()
  const LoginBackground = themeController?.components
    ? (themeController?.components['LoginBackground'] ?? DefaultLoginBackGround)
    : DefaultLoginBackGround

  const { handleModal, modal } = useContext(CustomModalContext)
  const [code, setCode] = useState('')
  const [error, setError] = useState(false)
  const [disableButtons, setDisableButtons] = useState(false)
  const [success, setSuccess] = useState(false)
  const [openBackdrop, setOpenBackdrop] = useState(false)
  const [backdropContent, setBackdropContent] = useState(<></>)

  const fontSize = menu?.currentMenu?.parametros?.FUENTE ?? 'x-large'
  const readings = menu?.currentMenu?.parametros?.LECTURAS ?? 'off'
  const autoclose = menu?.currentMenu?.parametros?.AUTOCIERRE
    ? parseInt(menu?.currentMenu?.parametros?.AUTOCIERRE)
    : 3000

  const handleClick = (value) => {
    const newCode = code + value
    setCode(newCode)
  }

  const handleDelete = () => {
    const newCode = code.slice(0, -1)
    setCode(newCode)
  }

  const cancelActions = () => {
    handleModal({ enabled: false })
  }

  const onError = () => {
    setError(true)
    setSuccess(false)
    setBackdropContent(
      <Paper
        sx={{
          backgroundColor: 'var(--mui-palette-error-main)',
          p: 4,
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4">
          {i18n.t('clkinerr', { defaultValue: 'Error registrando el fichaje' })}
        </Typography>
        <Typography variant="body1">
          {i18n.t('cporfinm', { defaultValue: 'Por favor, inténtelo de nuevo más tarde' })}
        </Typography>
      </Paper>,
    )
    setOpenBackdrop(true)
  }

  const onSuccess = (res) => {
    setError(false)
    setSuccess(true)
    setBackdropContent(
      <Paper
        sx={{
          backgroundColor: 'var(--mui-palette-success-main)',
          p: 4,
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4">
          {i18n.t('clkinsuc', { defaultValue: 'Fichaje registrado con éxito.' })}
        </Typography>
        {res ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
            <Avatar
              src={`/assets/images/${res.imagen}`}
              sx={{
                transition: '.3s',
                aspectRatio: '4/4',
                width: '80px',
                height: '80px',
                mr: 2,
              }}
            />
            <Typography variant="h5">{res.nombre}</Typography>
          </Box>
        ) : null}
      </Paper>,
    )
    setOpenBackdrop(true)
  }

  useEffect(() => {
    if (!success && !error) return

    setDisableButtons(true)

    timerRef.current = setTimeout(() => {
      setDisableButtons(false)
      setSuccess(false)
      setError(false)
      setCode('')
      timerRef.current = null
    }, autoclose)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [success, error])

  const cardRead = (result) => {
    if (code.length === 0 && result.length === 0) {
      enqueueSnackbar('Necesita introducir un código o escanear su QR', { variant: 'error' })
      return null
    }

    const card =
      code && code.length > 0
        ? String(code).padStart('0', code.length)
        : result && result.length > 0
          ? String(result).padStart('0', result.length)
          : null

    handleModal({
      type: ModalTypes.Custom,
      enabled: true,
      content: (
        <Paper sx={{ p: 4 }}>
          <PresenceActions
            loading={false}
            handleModal={handleModal}
            modal={modal}
            card={card}
            inColumns={true}
            fontSize={`${fontSize} !important`}
            onError={onError}
            onSuccess={onSuccess}
          />
          <Button
            variant="contained"
            color="error"
            size="large"
            onClick={cancelActions}
            sx={{ mt: 2 }}
            fullWidth
          >
            {i18n.t('general.cancelar', { defaultValue: 'Cancelar' })}
          </Button>
        </Paper>
      ),
    })
  }

  const manualClose = () => {
    if (timerRef.current) {
      setDisableButtons(false)
      setSuccess(false)
      setError(false)
      setCode('')

      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  return (
    <>
      <LoginBackground
        loading={false}
        onlyImage={true}
        centerContent={true}
        contentWidth={{
          xs: '90%',
          md: '75%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
          {themeController?.hide?.PanelFichajes?.Reloj ? (
            <></>
          ) : (
            <CustomClock showDate={true} timeTextVariant="h5" />
          )}
          <PanelInput value={code} error={error} success={success} fontSize={fontSize} />
          <PanelNumerico
            fontSize={fontSize}
            readings={readings}
            onClick={handleClick}
            onDelete={handleDelete}
            onCheck={cardRead}
            disableButtons={disableButtons}
          />
          <Button
            variant="text"
            size="large"
            sx={{ fontSize: fontSize, background: 'none' }}
            onClick={() => navigate('/login')}
          >
            {i18n.t('login.login', { defaultValue: 'Login' })}
          </Button>
        </Box>
      </LoginBackground>
      <CustomBackdrop
        variant={success ? 'success' : error ? 'error' : 'loading'}
        content={backdropContent}
        closable={true}
        autoclose={autoclose}
        open={openBackdrop}
        onHide={() => {
          manualClose()
          setOpenBackdrop(false)
        }}
      />
    </>
  )
}

export default PanelFichajes
